import { CheckboxInput, EmailInput, PasswordInput } from '@frontend/components/basic-forms';
import { CommonMessage } from '@frontend/lang';
import { useBrandingRepository } from '@frontend/repository';
import { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import { default as defaultLogo } from '../../../assets/images/telloport_logo_horizontal.png';
import Sso from '../sso/sso.component';
import useBasicLogin from './basic-login.controller';

export const ID = 'basic-login';
export interface Props {
    callback?: () => void;
}

// TODO: Translations

const BasicLogin = (props: Props) => {
    const viewProps = useBasicLogin(props);
    const { logo_navbar_expanded } = useBrandingRepository();

    const logo = useMemo(() => {
        if (logo_navbar_expanded) return logo_navbar_expanded;
        return defaultLogo;
    }, [logo_navbar_expanded]);
    return (
        <div
            id={ID}
            className='bg-background-surface flex h-dvh flex-col'>
            <div className='flex min-h-0 flex-1 flex-col items-center justify-center self-stretch'>
                <img
                    className='img-fluid w-40'
                    src={logo}
                />
            </div>

            <div className='bg-background-default border-border-default max-h-full shrink-0 overflow-y-auto border-t px-5 py-12'>
                <div className='inline-flex w-full flex-col items-center justify-start gap-12'>
                    <div className='flex flex-col items-center justify-start gap-2'>
                        <div className='text-foreground-default justify-start text-xl font-semibold'>
                            {
                                <FormattedMessage
                                    id='login.get-started'
                                    defaultMessage='Get started'
                                />
                            }
                        </div>
                        <div className='text-foreground-default justify-start text-center text-base font-medium'>
                            {
                                <FormattedMessage
                                    id='login.enter-company-credentials'
                                    defaultMessage='Enter your company credentials'
                                />
                            }
                        </div>
                    </div>

                    <form
                        className='flex flex-col items-start justify-start gap-6 self-stretch'
                        onSubmit={viewProps.onSubmit}>
                        <div className='flex w-full flex-col items-start justify-start gap-4'>
                            <EmailInput
                                required
                                label={CommonMessage.FORMS.EMAIL()}
                                errorMessage={
                                    <FormattedMessage
                                        id='login.enter-valid-email'
                                        defaultMessage='Please fill in a valid email.'
                                    />
                                }
                                autoFocus
                                value={viewProps.formValues.email}
                                onChange={(value) => viewProps.changeFormValues({ ...viewProps.formValues, email: value })}
                                isValidCallback={(value) => {
                                    if (viewProps.submitted) viewProps.changeEmailValid(value);
                                }}
                                submitted={viewProps.submitted}
                                valid={viewProps.emailValid}
                            />

                            <PasswordInput
                                required
                                label={CommonMessage.FORMS.PASSWORD}
                                errorMessage={
                                    <FormattedMessage
                                        id='login.enter-your-password'
                                        defaultMessage='Please fill in a your password.'
                                    />
                                }
                                value={viewProps.formValues.password}
                                onChange={(value) => viewProps.changeFormValues({ ...viewProps.formValues, password: value })}
                                isValidCallback={(value) => {
                                    if (viewProps.submitted) viewProps.changePasswordValid(value);
                                }}
                                noConditionedStyling
                                valid={viewProps.passwordValid}
                            />
                            <CheckboxInput
                                id='keep-logged-in'
                                value={viewProps.keepLoggedIn}
                                onChange={viewProps.changeKeepLoggedIn}
                                label={
                                    <FormattedMessage
                                        id='login.keep-me-logged-in'
                                        description='Checkbox label on the login page to enable automatic session token refresh'
                                        defaultMessage='Keep me logged in'
                                    />
                                }
                            />
                        </div>
                        <button
                            id='sign-in-button'
                            type='submit'
                            className='bg-brand-primary disabled:bg-brand-primary/70 inline-flex h-12 items-center justify-center self-stretch rounded-xl p-4'
                            disabled={
                                viewProps.formValues.email?.length === 0 ||
                                viewProps.formValues.password?.length === 0 ||
                                !viewProps.emailValid ||
                                !viewProps.passwordValid ||
                                viewProps.submitted
                            }>
                            <div className='text-background-surface justify-start text-base font-medium'>{CommonMessage.BUTTONS.LOGIN}</div>
                        </button>
                    </form>

                    <Sso
                        callback={props.callback}
                        keepLoggedIn={viewProps.keepLoggedIn}
                    />
                </div>
            </div>
        </div>
    );
};

export default BasicLogin;
